import { CommonModule } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { CdkDragDrop, DragDropModule, moveItemInArray } from "@angular/cdk/drag-drop";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { firstValueFrom } from "rxjs";

import TripAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/trip/trip.entity";

import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import { VisitOrderModeEnum, VISIT_ORDER_MODE_LABELS } from "app/common/enums/visit-order-mode.enum";
import { TripRouteService } from "app/modules/components/sales/trip/trip.service";
import {
  TripVisitOrderService,
  VisitOrderEntry,
} from "app/modules/components/sales/trip/trip-visit-order.service";
import { BusinessPartnerService } from "app/modules/components/business-partners/business-partner/business-partner.service";
import { TripStatusBadgeComponent } from "app/shared/components/trip-status-badge/trip-status-badge.component";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { TimeDifferencePipe } from "app/pipes/diference-time.pipe";

/** Datos de entrada del diálogo — SOLO el id, todo lo demás se pide fresco. */
export interface TripVisitOrderDialogData {
  tripMongoId: string;
}

/** Una fila ya resuelta (nombre + dirección) lista para pintar y arrastrar. */
export interface VisitOrderRow {
  businessPartnerMongoId: string;
  businessPartnerAddressMongoId: string;
  businessPartnerName: string;
  addressLabel: string;
  addressFound: boolean;
}

/**
 * Ver y editar el orden de visita guardado de un viaje (`prc-trip-visit-order`),
 * con datos de cliente y de viaje resueltos frescos — ver
 * fs-98-trip-visit-order-editor, design.md Decisión 1: nunca se reusan los
 * datos delgados del `element` de `trip-table`, ni sus `Order` (lazy, y
 * ambiguas si varias comparten socio).
 */
@Component({
  selector: "app-trip-visit-order-dialog",
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    TripStatusBadgeComponent,
    FormatOtherDateHourPipe,
    TimeDifferencePipe,
  ],
  templateUrl: "./trip-visit-order.dialog.html",
  styleUrl: "./trip-visit-order.dialog.scss",
})
export class TripVisitOrderDialog implements OnInit {
  loading = true;
  saving = false;
  savingMode = false;

  trip: Partial<TripAttributes> | null = null;
  rows: VisitOrderRow[] = [];
  hasVisitOrder = false;

  readonly visitOrderModeEnum = VisitOrderModeEnum;

  /** Etiqueta legible del modo actual (`Por subruta` / `Por viaje`). */
  get visitOrderModeLabel(): string {
    const mode = this.trip?.visitOrderMode;
    return mode != null ? VISIT_ORDER_MODE_LABELS[mode] : "";
  }

  constructor(
    private readonly dialogRef: MatDialogRef<TripVisitOrderDialog>,
    @Inject(MAT_DIALOG_DATA) public readonly data: TripVisitOrderDialogData,
    private readonly tripService: TripRouteService,
    private readonly tripVisitOrderService: TripVisitOrderService,
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly handlerMessage: HandlerMessageService
  ) {}

  ngOnInit(): void {
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const [visitOrderRes, tripRes] = await Promise.all([
        firstValueFrom(this.tripVisitOrderService.findByTrip(this.data.tripMongoId)),
        firstValueFrom(this.tripService.findOne(this.data.tripMongoId)),
      ]);

      this.trip = tripRes.data;

      const entries = visitOrderRes.data?.visitOrder ?? [];
      this.hasVisitOrder = entries.length > 0;
      this.rows = this.hasVisitOrder ? await this.resolveRows(entries) : [];
    } catch (err: any) {
      this.handlerMessage.error({
        message: err?.error?.message,
        alternativeMessage: "Error al cargar el orden de visita del viaje",
        statusCode: err?.status,
      });
    } finally {
      this.loading = false;
    }
  }

  /**
   * Resuelve nombre + dirección de cada entrada en UN SOLO batch a
   * `BusinessPartnerService` (ver design.md, Decisión 1) — nunca contra las
   * `Order` del viaje. La dirección se busca POR ENTRADA dentro de
   * `addresses[]` (no la primera del socio): dos entradas del mismo socio
   * pueden traer direcciones distintas.
   */
  private async resolveRows(entries: VisitOrderEntry[]): Promise<VisitOrderRow[]> {
    const sorted = [...entries].sort((a, b) => a.position - b.position);
    const ids = [...new Set(sorted.map((e) => String(e.businessPartnerMongoId)))];

    const bpRes = await firstValueFrom(this.businessPartnerService.findByIds(ids));
    const businessPartnerById = new Map(
      (bpRes.data ?? []).map((bp) => [String(bp._id), bp])
    );

    return sorted.map((entry) => {
      const businessPartnerMongoId = String(entry.businessPartnerMongoId);
      const businessPartnerAddressMongoId = String(entry.businessPartnerAddressMongoId);
      const bp = businessPartnerById.get(businessPartnerMongoId);
      const address = bp?.addresses?.find(
        (a) => String(a._id) === businessPartnerAddressMongoId
      );

      return {
        businessPartnerMongoId,
        businessPartnerAddressMongoId,
        businessPartnerName: bp?.name ?? "Socio de negocio no encontrado",
        addressLabel: address ? address.address || address.name : "Dirección no disponible",
        addressFound: !!address,
      };
    });
  }

  drop(event: CdkDragDrop<VisitOrderRow[]>): void {
    moveItemInArray(this.rows, event.previousIndex, event.currentIndex);
  }

  async save(): Promise<void> {
    if (!this.rows.length || this.saving) return;

    this.saving = true;
    try {
      await firstValueFrom(
        this.tripVisitOrderService.replace(
          this.data.tripMongoId,
          this.rows.map((r) => ({
            businessPartnerMongoId: r.businessPartnerMongoId,
            businessPartnerAddressMongoId: r.businessPartnerAddressMongoId,
          }))
        )
      );
      this.handlerMessage.success({ message: "Orden de visita guardado correctamente." });
    } catch (err: any) {
      this.handlerMessage.error({
        message: err?.error?.message,
        alternativeMessage: "Error al guardar el orden de visita",
        statusCode: err?.status,
      });
    } finally {
      this.saving = false;
    }
  }

  /** Handler del `mat-slide-toggle`: `true` = modo `TRIP`, `false` = `SUB_ROUTE`. */
  onVisitOrderModeToggle(checked: boolean): void {
    void this.onVisitOrderModeChange(
      checked ? VisitOrderModeEnum.TRIP : VisitOrderModeEnum.SUB_ROUTE
    );
  }

  /**
   * Cambia SOLO `visitOrderMode` (`PATCH trip/:id`) — nunca toca
   * `prc-trip-visit-order` (ver design.md, Decisión 5): son conceptos
   * desacoplados, igual que ya lo son en `ave-trip`.
   */
  async onVisitOrderModeChange(mode: VisitOrderModeEnum): Promise<void> {
    const tripId = this.trip?._id ? String(this.trip._id) : undefined;
    if (!tripId || this.savingMode) return;

    const previous = this.trip?.visitOrderMode;
    this.trip = { ...this.trip, visitOrderMode: mode };
    this.savingMode = true;
    try {
      await firstValueFrom(this.tripService.updateTrip({ visitOrderMode: mode }, tripId));
      this.handlerMessage.success({ message: "Modo de orden de visita actualizado." });
    } catch (err: any) {
      this.trip = { ...this.trip, visitOrderMode: previous };
      this.handlerMessage.error({
        message: err?.error?.message,
        alternativeMessage: "Error al actualizar el modo de orden de visita",
        statusCode: err?.status,
      });
    } finally {
      this.savingMode = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
